import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { allCategories, catLabel } from "@/lib/lore";
import { getTemplates, WORLD_SETTINGS, settingLabel } from "@/lib/templates";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useInvalidate } from "@/lib/queries";

export const Toggle = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-sm border px-2.5 py-1 text-xs transition-colors ${on ? "border-[#D97706] bg-[#D97706]/10 text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}>{children}</button>
);

export function WorldSettingsPicker({ uid, compact, disabled }) {
  const invalidate = useInvalidate();
  const [ws, setWs] = useState(getTemplates().settings);
  const [custom, setCustom] = useState("");
  const toggle = (k) => save(ws.includes(k) ? ws.filter((x) => x !== k) : [...ws, k]);
  const addCustom = (e) => {
    e.preventDefault();
    const name = custom.trim();
    const k = WORLD_SETTINGS.find(([, l]) => l.toLowerCase() === name.toLowerCase())?.[0] || `custom:${name}`;
    if (name && !ws.includes(k)) save([...ws, k]);
    setCustom("");
  };
  const save = async (next) => {
    setWs(next);
    try {
      const { data } = await api.put(`/universes/${uid}/world-settings`, { world_settings: next });
      if (data?.world_settings) setWs(data.world_settings);
      invalidate(["universe", uid], ["entities", uid], "entity");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="world-settings">
      {!compact && <span className="lf-label mr-1">World setting</span>}
      {WORLD_SETTINGS.map(([k, l]) => <Toggle key={k} on={ws.includes(k)} onClick={() => !disabled && toggle(k)} testid={`world-setting-${k}`}>{l}</Toggle>)}
      {ws.filter((k) => k.startsWith("custom:")).map((k) => <Toggle key={k} on onClick={() => !disabled && toggle(k)} testid={`world-setting-${k}`}>{settingLabel(k)} ×</Toggle>)}
      {!compact && !disabled && (
        <form onSubmit={addCustom} className="flex items-center gap-1">
          <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom…" className="h-7 w-32 text-xs" maxLength={40} data-testid="world-setting-custom-input" />
          <Button type="submit" size="sm" variant="ghost" className="h-7 px-2" disabled={!custom.trim()} data-testid="world-setting-custom-add"><Plus className="h-3.5 w-3.5" /></Button>
        </form>
      )}
    </div>
  );
}

function FieldRow({ f, i, upd, remove }) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2" data-testid={`template-field-${i}`}>
      <Input className="h-9" value={f.label} onChange={(e) => upd({ label: e.target.value, key: f.isNew ? e.target.value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_") : f.key })} data-testid={`template-field-label-${i}`} />
      <label className="flex items-center gap-1 text-xs text-[#A8A090]"><Checkbox checked={f.optional} onCheckedChange={(v) => upd({ optional: !!v })} data-testid={`template-field-optional-${i}`} />optional</label>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="max-w-[200px] truncate rounded-sm border border-[#2D3442] px-2.5 py-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid={`template-field-settings-${i}`}>
            {f.settings.length ? f.settings.map(settingLabel).join(", ") : "All world types"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 space-y-1.5">
          <p className="text-xs text-[#A8A090]">Only show this field in these world types (none = always).</p>
          {[...WORLD_SETTINGS, ...getTemplates().settings.filter((k) => k.startsWith("custom:")).map((k) => [k, settingLabel(k)])].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <Checkbox checked={f.settings.includes(k)} onCheckedChange={(v) => upd({ settings: v ? [...f.settings, k] : f.settings.filter((x) => x !== k) })} data-testid={`template-field-setting-${i}-${k}`} />{l}
            </label>
          ))}
        </PopoverContent>
      </Popover>
      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={remove} data-testid={`template-field-remove-${i}`}><X className="h-4 w-4" /></Button>
    </div>
  );
}

export function TemplateManager({ uid, onOpenChange }) {
  const invalidate = useInvalidate();
  const [cat, setCat] = useState("Character");
  const [fields, setFields] = useState((getTemplates().templates.Character || []).map((f) => ({ ...f })));
  const [busy, setBusy] = useState(false);
  const pick = (c) => { setCat(c); setFields((getTemplates().templates[c] || []).map((f) => ({ ...f }))); };
  const refresh = () => invalidate(["universe", uid], ["entities", uid], "entity");
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/universes/${uid}/templates/${encodeURIComponent(cat)}`, { fields: fields.filter((f) => f.label.trim()).map(({ isNew, ...f }) => ({ ...f, key: f.key || f.label })) });
      refresh();
      toast.success(`${catLabel(cat)} template saved`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    await api.delete(`/universes/${uid}/templates/${encodeURIComponent(cat)}`);
    refresh();
    toast.success("Reset to the default template");
    onOpenChange(false);
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto lf-scroll" data-testid="template-manager">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Recommended info</DialogTitle>
          <DialogDescription>Choose what each kind of entry should record. Fields tagged with a world setting only appear when your world has that setting.</DialogDescription>
        </DialogHeader>
        <WorldSettingsPicker uid={uid} />
        <Select value={cat} onValueChange={pick}>
          <SelectTrigger className="w-64" data-testid="template-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
        </Select>
        <div className="space-y-2" data-testid="template-fields">
          {fields.length === 0 && <p className="text-sm text-[#A8A090]">No recommended fields for this category yet.</p>}
          {fields.map((f, i) => <FieldRow key={i} f={f} i={i} upd={(p) => setFields(fields.map((x, j) => (j === i ? { ...x, ...p } : x)))} remove={() => setFields(fields.filter((_, j) => j !== i))} />)}
          <Button type="button" variant="ghost" size="sm" className="text-[#F59E0B]" onClick={() => setFields([...fields, { key: "", label: "", optional: false, settings: [], isNew: true }])} data-testid="template-add-field"><Plus className="h-3.5 w-3.5 mr-1" />Add field</Button>
        </div>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={reset} data-testid="template-reset-button"><RotateCcw className="h-4 w-4 mr-1.5" />Reset to default</Button>
          <Button onClick={save} disabled={busy} data-testid="template-save-button">{busy ? "Saving…" : "Save template"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
